import { getRedirectUrl, setIdP, setInvitationIntent, getRURL, getCsle } from './url-utils';
import { type IdpButtonProps } from '../components/idp-button';

import GoogleIcon from '../../media/btn-google.svg';
import MicrosoftIcon from '../../media/btn-microsoft.svg';
import MicrosoftWhiteIcon from '../../media/btn-microsoft-white.svg';
import FacebookIcon from '../../media/btn-facebook.svg';

export type IdpConfigParams = {
  // eslint-disable-next-line no-unused-vars
  onClick: (redirectUrl: string, provider: string, index: number) => void;
  index: number;
  logger: any;
  scenario: 'signin' | 'signup' | 'userInvitation';
  serverContext: any;
  // For the userInvitation scenario only: 'create' renders "Continue with X" copy
  // while 'signin' renders "Sign in with X" copy. The redirect behavior is identical.
  invitationVariant?: 'create' | 'signin';
  // For the userInvitation scenario only: when the invitation is AAD-enforced
  // (invitationFlags.msaSupported === false), the Microsoft button must point at the
  // server-provided AAD-only federated login URL (idP=AAD) instead of the default
  // callback URL, so MSA accounts are not offered.
  aadOnly?: boolean;
};

// eslint-disable-next-line no-unused-vars
export type IdpButtonConfig = IdpButtonProps & {
  identityProviders: string[];
};

export type IdpButtonConfigGetter = (params: IdpConfigParams) => IdpButtonConfig;

// Picks the localized button copy for a provider. For the userInvitation scenario the new
// client-side flow's 'create' and 'signin' variants both render "Sign in with X"; the legacy
// invitation view (no variant) keeps "Continue with X". Every other scenario renders "Sign up
// with X".
function getProviderText(
  scenario: IdpConfigParams['scenario'],
  invitationVariant: IdpConfigParams['invitationVariant'],
  texts: { signup: string; continueWith: string; signIn: string }
): string {
  if (scenario === 'userInvitation') {
    return invitationVariant === 'create' || invitationVariant === 'signin'
      ? texts.signIn
      : texts.continueWith;
  }
  return texts.signup;
}

function getUrl(
  scenario: IdpConfigParams['scenario'],
  serverContext: IdpConfigParams['serverContext'],
  idPValue: string,
  aadOnly?: boolean,
  invitationVariant?: IdpConfigParams['invitationVariant']
): string {
  if (scenario === 'userInvitation') {
    if (aadOnly) {
      // AAD-enforced invitation: use the server-provided AAD-only federated login URL
      // (enforces idP=AAD, disallows MSA). Its server-baked RURL lands on the generic
      // product page, though — not the invitation post-auth page — so after login the user
      // would leave the invitation flow. Point the federated-login RURL DIRECTLY at the
      // invitation callback (`cb` = /ActivateMsaUser?Code=…) so the endpoint returns the user
      // straight to complete activation. This mirrors the proven post-auth
      // `buildFederatedSignInUrl` pattern (accept-invitation-page/query-params.ts): the
      // callback must be the federated-login endpoint's own RURL, NOT nested inside the
      // baked inner landing URL — that inner marketing/login page (campaign/login/live) does
      // not honor a nested RURL, which is why the earlier nested rewrite dropped the user on
      // the generic welcome page after AAD auth.
      const aadOnlyUrl = serverContext?.Urls?.FederatedLoginAadOnly || '';
      const invitationCallback = getRedirectUrl();
      if (aadOnlyUrl && invitationCallback) {
        try {
          const baseUrl = new URL(aadOnlyUrl);
          // Drop any pre-existing RURL (case-insensitive — the server binds it case-
          // insensitively) so we don't leave a stale/duplicate value, then set it to the
          // absolute invitation callback.
          Array.from(baseUrl.searchParams.keys())
            .filter(key => key.toLowerCase() === 'rurl')
            .forEach(key => baseUrl.searchParams.delete(key));
          baseUrl.searchParams.set('RURL', invitationCallback);
          return baseUrl.toString();
        } catch (e) {
          // Fall back to the server URL as-is if it isn't a parseable URL.
          return aadOnlyUrl;
        }
      }
      return aadOnlyUrl;
    }
    // redirect to the cb param value in the current URL
    const url = getRedirectUrl();
    const withIdP = setIdP(url, idPValue);
    // Only the "Create an account" variant carries intent=create, so the post-auth page knows to
    // show the "existing profile found" dialog when it turns out we already know this user. The
    // "Use an existing account" (signin) variant keeps the existing silent auto-activate behavior.
    return invitationVariant === 'create' ? setInvitationIntent(withIdP, 'create') : withIdP;
  }

  const {
    FederatedLogin,
    PMaxLiteSignup,
    GoogleSignup,
    FacebookSignup,
  } = serverContext?.Urls || {};

  if (scenario === 'signin') {
    const rurl = getRURL();
    // `csle` (ClientSideLoginError test alias) must ride on the post-auth /login/live landing so the
    // server reads it there (server PR AdsAppUI#7016976). The inner RURL of FederatedLogin IS that
    // landing, so stamp csle directly on it — the business RURL stays nested inside.
    const csle = getCsle();
    const baseUrl = new URL(FederatedLogin);

    if (rurl || csle) {
      try {
        // The inner RURL of FederatedLogin is the post-auth login landing. Update it to redirect to
        // the business RURL after successful login, and carry the csle alias for the server decision.
        const successfulLoginUrl = new URL(baseUrl.searchParams.get('RURL'));
        if (rurl) {
          successfulLoginUrl.searchParams.set('RURL', rurl);
        }
        if (csle) {
          successfulLoginUrl.searchParams.set('csle', csle);
        }

        baseUrl.searchParams.set('RURL', successfulLoginUrl.toString());
      } catch (e) {
        // do nothing
      }
    }

    baseUrl.searchParams.set('idP', idPValue);
    return baseUrl.toString();
  }

  switch (idPValue) {
    case 'Google':
      return GoogleSignup;
    case 'Facebook':
      return FacebookSignup;
    case 'MSA':
    default:
      return PMaxLiteSignup;
  }
}

export const getMicrosoftConfig: IdpButtonConfigGetter = (params) => {
  const {
    scenario,
    serverContext,
    onClick,
    index,
    invitationVariant,
    aadOnly,
  } = params;

  const redirectUrl = getUrl(scenario, serverContext, 'MSA', aadOnly, invitationVariant);

  // In the userInvitation flow the Microsoft button is highlighted as the primary
  // (blue) option and uses the white logo so it reads against the blue background.
  const isInvitation = scenario === 'userInvitation';

  const config: IdpButtonConfig = {
    identityProviders: ['MSA', 'AAD'],
    buttonClassName: 'sign-up-button-ms',
    dataActionName: 'ms-sign-up',
    imageClassName: 'ms-sign-up-image',
    imageSrc: isInvitation ? MicrosoftWhiteIcon : MicrosoftIcon,
    textClassName: 'ms-sign-up-text',
    textContent: getProviderText(scenario, invitationVariant, {
      signup: _TL_('Sign up with Microsoft'),
      continueWith: _TL_('Continue with Microsoft'),
      signIn: _TL_('Sign in with Microsoft'),
    }),
    appearance: isInvitation ? 'primary' : 'secondary',
    onClick: () => onClick(redirectUrl, 'Microsoft', index),
  };

  if (scenario === 'signin') {
    config.buttonClassName = 'sign-in-button-ms';
    config.dataActionName = 'ms-sign-in';
    config.imageClassName = 'ms-sign-in-image';
    config.textClassName = 'ms-sign-in-text';
    config.textContent = _TL_('Sign in with Microsoft');
  }

  return config;
};

export const getGoogleConfig: IdpButtonConfigGetter = (params) => {
  const {
    scenario,
    serverContext,
    onClick,
    index,
    invitationVariant,
  } = params;

  const redirectUrl = getUrl(scenario, serverContext, 'Google', false, invitationVariant);

  const config = {
    identityProviders: ['Google'],
    buttonClassName: 'sign-up-button-google',
    dataActionName: 'google-sign-up',
    imageClassName: 'google-sign-up-image',
    imageSrc: GoogleIcon,
    textClassName: 'google-sign-up-text',
    textContent: getProviderText(scenario, invitationVariant, {
      signup: _TL_('Sign up with Google'),
      continueWith: _TL_('Continue with Google'),
      signIn: _TL_('Sign in with Google'),
    }),
    onClick: () => onClick(redirectUrl, 'Google', index),
  };

  if (scenario === 'signin') {
    config.buttonClassName = 'sign-in-button-google';
    config.dataActionName = 'google-sign-in';
    config.imageClassName = 'google-sign-in-image';
    config.textClassName = 'google-sign-in-text';
    config.textContent = _TL_('Sign in with Google');
  }

  return config;
};

export const getFacebookConfig: IdpButtonConfigGetter = (params) => {
  const {
    scenario,
    serverContext,
    onClick,
    index,
    invitationVariant,
  } = params;

  const redirectUrl = getUrl(scenario, serverContext, 'Facebook', false, invitationVariant);

  const config = {
    identityProviders: ['Facebook'],
    buttonClassName: 'sign-up-button-facebook',
    dataActionName: 'facebook-sign-up',
    imageClassName: 'facebook-sign-up-image',
    imageSrc: FacebookIcon,
    textClassName: 'facebook-sign-up-text',
    textContent: getProviderText(scenario, invitationVariant, {
      signup: _TL_('Sign up with Facebook'),
      continueWith: _TL_('Continue with Facebook'),
      signIn: _TL_('Sign in with Facebook'),
    }),
    onClick: () => onClick(redirectUrl, 'Facebook', index),
  };

  if (scenario === 'signin') {
    config.buttonClassName = 'sign-in-button-facebook';
    config.dataActionName = 'facebook-sign-in';
    config.imageClassName = 'facebook-sign-in-image';
    config.textClassName = 'facebook-sign-in-text';
    config.textContent = _TL_('Sign in with Facebook');
  }

  return config;
};
